import { useEffect, useState } from "react";
import { api, API, getSessionToken } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SendDialog } from "@/components/SendDialog";
import { Download, Inbox, CheckCircle2, Loader2, Send } from "lucide-react";

export default function History() {
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);

  const load = () => api.get("/runs").then((r) => setItems(r.data)).catch(() => setItems([]));

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const download = (id) => window.open(`${API}/runs/${id}/download?token=${getSessionToken()}`, "_blank");
  const openResend = (r) => { setSelected(r); setSendOpen(true); };

  if (items === null) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  }

  if (!items.length) {
    return (
      <div className="max-w-md mx-auto text-center py-24">
        <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-slate-800 grid place-items-center mx-auto mb-4">
          <Inbox className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="font-display font-semibold text-slate-900 dark:text-slate-100">Nessuna run elaborata</h3>
        <p className="text-sm text-slate-500 mt-1">Carica una o più Delivery Notes per iniziare.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-800">
                <th className="px-5 py-3">Documenti SAP</th>
                <th className="px-5 py-3">Consegne</th>
                <th className="px-5 py-3">Destinatari / CC</th>
                <th className="px-5 py-3">Inviato il</th>
                <th className="px-5 py-3">Stato</th>
                <th className="px-5 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const totalItems = (r.notes || []).reduce((s, n) => s + (n.items?.length || 0), 0);
                const docs = (r.notes || []).map((n) => n.header?.document_id || n.header?.delivery_note_id).filter(Boolean);
                return (
                  <tr key={r.id} data-testid={`history-row-${r.id}`} className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40 align-top">
                    <td className="px-5 py-3">
                      <div className="font-mono-data font-medium text-slate-900 dark:text-slate-100 truncate max-w-[220px]">
                        {docs.slice(0, 3).join(", ")}{docs.length > 3 ? ` +${docs.length - 3}` : ""}
                      </div>
                      <div className="text-xs text-slate-400">
                        {new Date(r.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
                      {r.notes?.length || 0} <span className="text-slate-400">({totalItems} art.)</span>
                    </td>
                    <td className="px-5 py-3">
                      {r.recipients?.length ? (
                        <div className="space-y-0.5">
                          <div className="text-slate-700 dark:text-slate-300 truncate max-w-[220px]">{r.recipients.join(", ")}</div>
                          {r.cc?.length ? <div className="text-xs text-slate-400 truncate max-w-[220px]">CC: {r.cc.join(", ")}</div> : null}
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-5 py-3 text-slate-500 whitespace-nowrap">
                      {r.sent_at ? new Date(r.sent_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button data-testid={`history-download-${r.id}`} size="sm" variant="ghost" onClick={() => download(r.id)} className="gap-1.5 text-slate-500 hover:text-blue-600">
                          <Download className="h-4 w-4" /> XLSX
                        </Button>
                        <Button data-testid={`history-resend-${r.id}`} size="sm" variant="ghost" onClick={() => openResend(r)} className="gap-1.5 text-slate-500 hover:text-emerald-600">
                          <Send className="h-4 w-4" /> {r.status === "sent" ? "Reinvia" : "Invia"}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <SendDialog run={selected} settings={settings} open={sendOpen} onOpenChange={setSendOpen} onSent={() => load()} />
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const map = {
    draft: { label: "Bozza", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
    sent: { label: "Inviato", cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" },
    error: { label: "Errore", cls: "bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300" },
  };
  const s = map[status] || map.draft;
  return (
    <Badge className={`${s.cls} border-0 gap-1`}>
      {status === "sent" && <CheckCircle2 className="h-3 w-3" />}
      {s.label}
    </Badge>
  );
};
